import React, { useEffect, useState } from 'react';
import BackButton from "../components/BackButton";
import FishingForecastService from '../services/FishingForecasts';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function tomorrowStr() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

function maxDateStr() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

function ScoreBadge({ score }) {
  let bg = '#6c757d';
  if (score >= 70) bg = '#198754';
  else if (score >= 50) bg = '#ffc107';
  else if (score > 0) bg = '#dc3545';

  return (
    <span style={{
      backgroundColor: bg,
      color: score >= 50 && score < 70 ? '#000' : '#fff',
      padding: '3px 8px',
      borderRadius: '10px',
      fontSize: '0.85em',
      fontWeight: 'bold',
      display: 'inline-block',
    }}>
      {score > 0 ? score.toFixed(1) : 'DQ'}
    </span>
  );
}

function WaterTypeBadge({ type }) {
  const colors = {
    river: '#0d6efd',
    creek: '#0dcaf0',
    reservoir: '#6f42c1',
    lake: '#198754',
  };
  return (
    <span style={{
      backgroundColor: colors[type] || '#6c757d',
      color: '#fff',
      padding: '1px 5px',
      borderRadius: '4px',
      fontSize: '0.65em',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      marginLeft: '4px',
    }}>
      {type}
    </span>
  );
}

function FreshnessIndicator({ fetchedAt }) {
  if (!fetchedAt) return <span style={{ color: '#999', fontSize: '0.85em' }}>No data yet</span>;
  const now = new Date();
  const fetched = new Date(fetchedAt);
  const hoursAgo = (now - fetched) / (1000 * 60 * 60);
  let color = '#198754';
  let label = 'Fresh';
  if (hoursAgo > 48) { color = '#dc3545'; label = 'Stale'; }
  else if (hoursAgo > 24) { color = '#ffc107'; label = 'Aging'; }
  return (
    <span>
      <span style={{
        display: 'inline-block', width: '8px', height: '8px',
        borderRadius: '50%', backgroundColor: color, marginRight: '6px',
      }} />
      <span style={{ fontSize: '0.85em' }}>
        {label} — last fetched {formatDate(fetchedAt)}
      </span>
    </span>
  );
}

const CELL_STYLE = {
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'normal',
  verticalAlign: 'middle',
  textAlign: 'center',
  fontSize: '0.85em',
};

const FishingForecast = () => {
  const [forecasts, setForecasts] = useState([]);
  const [selectedDate, setSelectedDate] = useState(tomorrowStr());
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sortField, setSortField] = useState('composite_score');
  const [sortDir, setSortDir] = useState('desc');

  function loadForecasts(date) {
    setLoading(true);
    FishingForecastService.getForecasts(date).then((resp) => {
      setForecasts(resp.forecasts || []);
      setLastUpdated(resp.last_updated);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => {
    loadForecasts(selectedDate);
  }, [selectedDate]);

  function handleSort(field) {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'composite_score' ? 'desc' : 'asc');
    }
  }

  function sortedForecasts() {
    return [...forecasts].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (aVal == null) aVal = sortDir === 'asc' ? Infinity : -Infinity;
      if (bVal == null) bVal = sortDir === 'asc' ? Infinity : -Infinity;
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  function handleRefresh() {
    setRefreshing(true);
    FishingForecastService.refreshForecasts().then(() => {
      setTimeout(() => {
        setRefreshing(false);
        loadForecasts(selectedDate);
      }, 40000); // 35s for fetch + buffer
    });
  }

  function sortArrow(field) {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  const sorted = sortedForecasts();

  return (
    <div className="m-5">
      <div className="container-fluid">
        <BackButton />
        <h2 className="text-center">Top Fishing Spots</h2>

        {/* Controls */}
        <div className="row mb-3 align-items-center">
          <div className="col-md-3">
            <label className="form-label mb-1" style={{ fontWeight: 'bold', fontSize: '0.9em' }}>
              Forecast Date
            </label>
            <input
              type="date"
              className="form-control"
              value={selectedDate}
              min={todayStr()}
              max={maxDateStr()}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
          <div className="col-md-5 pt-4">
            <FreshnessIndicator fetchedAt={lastUpdated} />
          </div>
          <div className="col-md-4 pt-4 text-end">
            <span className="text-muted me-3">
              {loading ? 'Loading...' : `${forecasts.length} locations`}
            </span>
            <button
              className="btn btn-sm btn-success"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? 'Fetching Forecasts (~35s)...' : 'Refresh Forecasts'}
            </button>
          </div>
        </div>

        {/* Empty state */}
        {!loading && forecasts.length === 0 && (
          <div className="alert alert-info text-center">
            No forecast data available yet. Click <strong>Refresh Forecasts</strong> to fetch weather data from Open-Meteo for all locations.
            This takes about 35 seconds.
          </div>
        )}

        {/* Legend */}
        {forecasts.length > 0 && (
          <div className="mb-2" style={{ fontSize: '0.8em' }}>
            <span style={{ backgroundColor: '#d4edda', padding: '2px 8px', borderRadius: '4px', marginRight: '10px' }}>
              Top 20
            </span>
            <span className="me-3">Score: </span>
            <span style={{ backgroundColor: '#198754', color: '#fff', padding: '1px 6px', borderRadius: '8px', marginRight: '4px', fontSize: '0.9em' }}>70+</span>
            <span style={{ backgroundColor: '#ffc107', color: '#000', padding: '1px 6px', borderRadius: '8px', marginRight: '4px', fontSize: '0.9em' }}>50-69</span>
            <span style={{ backgroundColor: '#dc3545', color: '#fff', padding: '1px 6px', borderRadius: '8px', marginRight: '4px', fontSize: '0.9em' }}>&lt;50</span>
            <span style={{ backgroundColor: '#6c757d', color: '#fff', padding: '1px 6px', borderRadius: '8px', fontSize: '0.9em' }}>DQ</span>
          </div>
        )}

        {/* Table */}
        {forecasts.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="table table-hover table-bordered table-striped table-sm" style={{ tableLayout: 'fixed', width: '100%' }}>
              <colgroup>
                <col style={{ width: '45px' }} />   {/* Rank */}
                <col style={{ width: '140px' }} />  {/* Town */}
                <col style={{ width: '160px' }} />  {/* Nearest Water */}
                <col style={{ width: '65px' }} />   {/* Map */}
                <col style={{ width: '65px' }} />   {/* Score */}
                <col style={{ width: '55px' }} />   {/* High */}
                <col style={{ width: '55px' }} />   {/* Low */}
                <col style={{ width: '55px' }} />   {/* Wind */}
                <col style={{ width: '55px' }} />   {/* Gusts */}
                <col style={{ width: '55px' }} />   {/* Precip */}
                <col style={{ width: '55px' }} />   {/* Precip % */}
                <col style={{ width: '55px' }} />   {/* Snow */}
                <col style={{ width: '55px' }} />   {/* Cloud */}
                <col style={{ width: '55px' }} />   {/* Humidity */}
                <col style={{ width: '110px' }} />  {/* Weather */}
                <col style={{ width: '110px' }} />  {/* Sunrise/Sunset */}
              </colgroup>
              <thead className="table-secondary">
                <tr>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('composite_score')}>
                    #{sortArrow('composite_score')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer', textAlign: 'left' }} onClick={() => handleSort('town')}>
                    Town{sortArrow('town')}
                  </th>
                  <th style={{ ...CELL_STYLE, textAlign: 'left' }}>Nearest Water</th>
                  <th style={CELL_STYLE}>Map</th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('composite_score')}>
                    Score{sortArrow('composite_score')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('high_temp_f')}>
                    High{sortArrow('high_temp_f')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('low_temp_f')}>
                    Low{sortArrow('low_temp_f')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('max_wind_mph')}>
                    Wind{sortArrow('max_wind_mph')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('wind_gusts_mph')}>
                    Gust{sortArrow('wind_gusts_mph')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('precip_inches')}>
                    Precip{sortArrow('precip_inches')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('precip_probability')}>
                    P%{sortArrow('precip_probability')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('snow_depth_inches')}>
                    Snow{sortArrow('snow_depth_inches')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('cloud_cover_pct')}>
                    Cloud{sortArrow('cloud_cover_pct')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('humidity_pct')}>
                    Hum{sortArrow('humidity_pct')}
                  </th>
                  <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('weather_description')}>
                    Weather{sortArrow('weather_description')}
                  </th>
                  <th style={CELL_STYLE}>Rise / Set</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((f, idx) => {
                  const isTop20 = idx < 20 && sortField === 'composite_score' && sortDir === 'desc';
                  return (
                    <tr key={f.id} style={isTop20 ? { backgroundColor: '#d4edda' } : {}}>
                      <td style={CELL_STYLE}>
                        <strong>{idx + 1}</strong>
                      </td>
                      <td style={{ ...CELL_STYLE, textAlign: 'left', fontWeight: isTop20 ? 'bold' : 'normal' }}>
                        {f.town}
                        {f.elevation && (
                          <div style={{ fontSize: '0.75em', color: '#888' }}>{f.elevation.toLocaleString()}'</div>
                        )}
                      </td>
                      <td style={{ ...CELL_STYLE, textAlign: 'left' }}>
                        {f.nearest_water}
                        <WaterTypeBadge type={f.water_type} />
                      </td>
                      <td style={CELL_STYLE}>
                        <a
                          href={`https://www.google.com/maps?q=${f.latitude},${f.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`${f.latitude}, ${f.longitude}`}
                          style={{ fontSize: '0.75em' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {parseFloat(f.latitude).toFixed(2)}, {parseFloat(f.longitude).toFixed(2)}
                        </a>
                      </td>
                      <td style={CELL_STYLE}>
                        <ScoreBadge score={f.composite_score} />
                      </td>
                      <td style={{ ...CELL_STYLE, color: f.high_temp_f < 40 ? '#dc3545' : f.high_temp_f > 85 ? '#dc3545' : '#000' }}>
                        {f.high_temp_f.toFixed(0)}°
                      </td>
                      <td style={CELL_STYLE}>{f.low_temp_f.toFixed(0)}°</td>
                      <td style={{ ...CELL_STYLE, color: f.max_wind_mph > 15 ? '#dc3545' : f.max_wind_mph > 10 ? '#fd7e14' : '#000' }}>
                        {f.max_wind_mph.toFixed(0)}
                      </td>
                      <td style={CELL_STYLE}>{f.wind_gusts_mph.toFixed(0)}</td>
                      <td style={{ ...CELL_STYLE, color: f.precip_inches > 0 ? '#dc3545' : '#000' }}>
                        {f.precip_inches > 0 ? f.precip_inches.toFixed(2) : '0'}
                      </td>
                      <td style={{ ...CELL_STYLE, color: f.precip_probability > 50 ? '#dc3545' : f.precip_probability > 30 ? '#fd7e14' : '#000' }}>
                        {f.precip_probability}%
                      </td>
                      <td style={{ ...CELL_STYLE, color: f.snow_depth_inches > 0 ? '#0d6efd' : '#000' }}>
                        {f.snow_depth_inches > 0 ? f.snow_depth_inches.toFixed(1) : '0'}
                      </td>
                      <td style={CELL_STYLE}>{f.cloud_cover_pct}%</td>
                      <td style={CELL_STYLE}>{f.humidity_pct}%</td>
                      <td style={{ ...CELL_STYLE, fontSize: '0.8em' }}>{f.weather_description}</td>
                      <td style={{ ...CELL_STYLE, fontSize: '0.75em' }}>
                        {f.sunrise && <div>{f.sunrise}</div>}
                        {f.sunset && <div>{f.sunset}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default FishingForecast;
